import React from "react";
import { Button, Col, Row } from "react-bootstrap";
import adminTheme from "../admin-theme.module.css";

export type EdmWorkspaceSegment<T extends string = string> = {
  id: T;
  label: string;
  subtitle?: React.ReactNode;
  content: React.ReactNode;
};

type Props<T extends string = string> = {
  header: React.ReactNode;
  alerts?: React.ReactNode;
  overview: React.ReactNode;
  segments: Array<EdmWorkspaceSegment<T>>;
  activeSegment: T;
  onSegmentChange: (segmentId: T) => void;
  rail: React.ReactNode;
};

export const EdmWorkspaceShell = <T extends string = string>({
  header,
  alerts,
  overview,
  segments,
  activeSegment,
  onSegmentChange,
  rail,
}: Props<T>) => {
  const activeSegmentConfig = segments.find((segment) => segment.id === activeSegment) || segments[0];
  const activePanelId = activeSegmentConfig ? `edm-workspace-panel-${String(activeSegmentConfig.id)}` : undefined;
  const activeTabId = activeSegmentConfig ? `edm-workspace-tab-${String(activeSegmentConfig.id)}` : undefined;

  return (
    <div className="container py-4 text-white">
      {header}
      {alerts ? <div className="d-flex flex-column gap-3 mb-4">{alerts}</div> : null}

      <Row className="g-4 align-items-start">
        <Col lg={7}>
          {overview}

          <div className={`${adminTheme.workspaceSegmentBar} mb-3`} role="tablist" aria-label="EDM workspace sections">
            {segments.map((segment) => {
              const isActive = segment.id === activeSegment;
              const tabId = `edm-workspace-tab-${String(segment.id)}`;
              const panelId = `edm-workspace-panel-${String(segment.id)}`;

              return (
              <Button
                key={segment.id}
                variant="link"
                type="button"
                role="tab"
                id={tabId}
                aria-selected={isActive}
                aria-controls={panelId}
                className={`${adminTheme.workspaceSegmentButton} ${isActive ? adminTheme.workspaceSegmentButtonActive : ""}`}
                onClick={() => onSegmentChange(segment.id)}
              >
                <span className={adminTheme.workspaceSegmentLabel}>{segment.label}</span>
              </Button>
              );
            })}
          </div>

          {activeSegmentConfig?.subtitle ? (
            <div className="text-white-50 small mb-3">{activeSegmentConfig.subtitle}</div>
          ) : null}

          {activeSegmentConfig ? (
            <div role="tabpanel" id={activePanelId} aria-labelledby={activeTabId}>
              {activeSegmentConfig.content}
            </div>
          ) : null}
        </Col>

        <Col lg={5}>
          <div style={{ position: "sticky", top: "1rem" }}>
            {rail}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default EdmWorkspaceShell;
